import React, { useEffect, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { Search } from 'lucide-react'

import { ElpPromotionFilter } from '~/app/common/enums/elp-promotion-filter'
import { useTranslation } from '~/app/i18n/client'
import { useApi } from '../hooks/use-api'
import { useElpPromotions } from '../hooks/use-elp-promotions'
import useUnauthRedirect from '../hooks/use-unauth-redirect'
import { Loading } from '../maple/pages/checkout/common/loading'
import { ElpPromotionCard } from './elp-promotion-card'

type ElpPromotionsProps = {
  content: any
  showHeader?: boolean
}

const ElpPromotions: React.FC<ElpPromotionsProps> = ({
  content,
  showHeader = true,
}: ElpPromotionsProps) => {
  const { sessionLoading, isAuthenticated } = useUnauthRedirect(true)

  const { api } = useApi()
  const { t: translate } = useTranslation()
  const [selectedOption, setSelectedOption] =
    React.useState<ElpPromotionFilter>(ElpPromotionFilter.All)
  const [searchQuery, setSearchQuery] = React.useState<string>('')
  const [hash, setHash] = useState<string>('')
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const filterOptions: ElpPromotionFilter[] = [
    ElpPromotionFilter.All,
    ElpPromotionFilter.Available,
    ElpPromotionFilter.Enrolled,
  ]

  const {
    data: elpOffers,
    isLoading: elpOffersLoading,
    refetch: refetchOffers,
    isRefetching,
  } = api.elpPromotions.getPromotions.useQuery(null, {
    enabled: isAuthenticated && !sessionLoading,
  })

  const { promotions } = useElpPromotions({
    elpOffers: elpOffers?.offers,
    promotionsContent: content?.promotions,
    filterOption: selectedOption,
    searchQuery: searchQuery?.trim(),
    offersLoading: elpOffersLoading || isRefetching,
  })

  const isLoading = sessionLoading || elpOffersLoading || isRefetching

  useEffect(() => {
    setHash(window.location.hash)
  }, [pathname, searchParams])

  useEffect(() => {
    if (hash && !isLoading) {
      const targetCard = document.querySelector(
        `[data-card-id*="${hash?.replace('#', '')}"]`,
      )

      if (targetCard) {
        const headerOffset = 120
        const elementPosition = targetCard.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.scrollY - headerOffset

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        })
      }
    }
  }, [hash, isLoading])

  if (sessionLoading || elpOffersLoading || isRefetching) {
    return <Loading />
  }

  return (
    <div className="col-span-2 mx-auto overflow-hidden">
      {showHeader && (
        <div className="w-full lg:w-[1140px] lg:max-w-[1140px]">
          <div className="flex flex-col px-4 lg:flex-row lg:justify-between lg:p-0">
            <div className="mb-8 flex flex-col lg:m-0">
              <div className="text-2xl font-semibold">
                {translate('elpPromotions.heading')}
              </div>
              <div className="text-base text-gray-500">
                {translate('elpPromotions.subtitle')}
              </div>
            </div>

            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-2">
              <div className="relative flex items-center">
                <Search
                  size={18}
                  className="pointer-events-none absolute left-3 text-gray-500"
                />
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full rounded-lg border border-gray-300 p-2 pl-10 focus:outline-none focus:ring-2 focus:ring-tenant-primary-color disabled:cursor-not-allowed disabled:bg-gray-100"
                  disabled={!elpOffers?.offers?.length}
                  onChange={(e) => setSearchQuery(e?.target?.value ?? '')}
                />
              </div>
              <div className="rounded-md border border-gray-200 py-1">
                <div className="flex justify-evenly">
                  {filterOptions?.map((option) => (
                    <button
                      key={option}
                      className={`mx-2 w-full rounded-md px-4 py-1 text-sm font-semibold uppercase text-gray-400 ${
                        selectedOption === option
                          ? 'bg-tenant-primary-color text-white'
                          : ''
                      }`}
                      onClick={() => setSelectedOption(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={showHeader ? 'mt-12' : ''}>
        {promotions?.length ? (
          <div className="grid grid-cols-1 gap-8 px-4 lg:grid-cols-3 lg:px-0">
            {promotions?.map((promotion: any, index: number) => (
              <React.Fragment key={`${promotion?.instanceIds}-${index}`}>
                <ElpPromotionCard
                  promotion={promotion}
                  refetchOffers={refetchOffers}
                />
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="text-xl font-semibold text-gray-400">
              {translate('elpPromotions.noOffers')}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export { ElpPromotions }
